import { useEffect, useRef, useState } from "react";
import { Loader2, Check, ArrowRight, X } from "lucide-react";
import { joinWaitlist, checkHandle, getHandleSuggestions, getPublicConfig } from "@/lib/api";
import { toast } from "sonner";
import ShareRow from "@/components/site/ShareRow";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const WaitlistForm = ({ id = "waitlist-form", compact = false }) => {
  const [handle, setHandle] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  // status: idle | checking | available | premium | taken | reserved | invalid
  const [handleStatus, setHandleStatus] = useState("idle");
  const [suggestions, setSuggestions] = useState([]);
  const debounceRef = useRef(null);
  const requestSeq = useRef(0);

  // Cloudflare Turnstile (invisible) — resolves a fresh token on demand.
  const turnstileContainerRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);
  const turnstilePendingRef = useRef(null);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState("");

  // Fetch the Turnstile site key from backend config (single source of truth).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await getPublicConfig();
        if (!cancelled) setTurnstileSiteKey(cfg?.turnstile_site_key || "");
      } catch {
        if (!cancelled) setTurnstileSiteKey("");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!turnstileSiteKey || !turnstileContainerRef.current) return;
    let cancelled = false;

    const render = () => {
      if (cancelled || !window.turnstile || turnstileWidgetIdRef.current !== null) return;
      turnstileWidgetIdRef.current = window.turnstile.render(
        turnstileContainerRef.current,
        {
          sitekey: turnstileSiteKey,
          size: "invisible",
          action: "waitlist",
          retry: "auto",
          "refresh-expired": "auto",
          callback: (token) => {
            const p = turnstilePendingRef.current;
            turnstilePendingRef.current = null;
            if (p) p.resolve(token);
          },
          "error-callback": () => {
            const p = turnstilePendingRef.current;
            turnstilePendingRef.current = null;
            if (p) p.reject(new Error("turnstile-error"));
          },
          "expired-callback": () => {
            const p = turnstilePendingRef.current;
            turnstilePendingRef.current = null;
            if (p) p.reject(new Error("turnstile-expired"));
          },
        }
      );
    };

    if (window.turnstile) {
      render();
    } else {
      const poll = setInterval(() => {
        if (window.turnstile) {
          clearInterval(poll);
          render();
        }
      }, 100);
      return () => {
        cancelled = true;
        clearInterval(poll);
      };
    }

    return () => {
      cancelled = true;
      try {
        if (window.turnstile && turnstileWidgetIdRef.current !== null) {
          window.turnstile.remove(turnstileWidgetIdRef.current);
        }
      } catch {
        /* ignore */
      }
      turnstileWidgetIdRef.current = null;
    };
  }, [turnstileSiteKey]);

  const requestTurnstileToken = () =>
    new Promise((resolve, reject) => {
      if (!turnstileSiteKey) {
        // Site key not configured — fail closed so no request slips through
        reject(new Error("turnstile-missing-sitekey"));
        return;
      }
      if (!window.turnstile || turnstileWidgetIdRef.current === null) {
        reject(new Error("turnstile-not-ready"));
        return;
      }
      turnstilePendingRef.current = { resolve, reject };
      try {
        window.turnstile.reset(turnstileWidgetIdRef.current);
        window.turnstile.execute(turnstileWidgetIdRef.current);
      } catch (e) {
        turnstilePendingRef.current = null;
        reject(e);
      }
    });

  const sanitizeHandle = (v) =>
    v.toLowerCase().replace(/[^a-z0-9.-]/g, "").slice(0, 30);

  const handleOk = handleStatus === "available" || handleStatus === "premium";
  const emailLooksValid = EMAIL_RE.test(email.trim());

  // Debounced live availability check
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!handle) {
      setHandleStatus("idle");
      setSuggestions([]);
      return;
    }
    if (handle.length < 4) {
      setHandleStatus("invalid");
      setSuggestions([]);
      return;
    }
    setHandleStatus("checking");
    setSuggestions([]);
    const seq = ++requestSeq.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await checkHandle(handle);
        if (seq !== requestSeq.current) return;
        if (data.available) {
          setHandleStatus(data.tier === "premium" ? "premium" : "available");
        } else {
          const reasonCode = data.reason || "invalid";
          setHandleStatus(reasonCode);
          if (
            reasonCode === "taken" ||
            reasonCode === "reserved" ||
            reasonCode === "trademarked" ||
            reasonCode === "not allowed"
          ) {
            try {
              const sg = await getHandleSuggestions(handle, 3);
              if (seq === requestSeq.current) {
                setSuggestions(sg?.suggestions || []);
              }
            } catch {
              if (seq === requestSeq.current) setSuggestions([]);
            }
          }
        }
      } catch {
        if (seq === requestSeq.current) setHandleStatus("idle");
      }
    }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [handle]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!handle || !handleOk) {
      toast.error("Pick an available handle first.");
      return;
    }
    if (!emailLooksValid) {
      toast.error("Enter a valid email.");
      return;
    }
    setLoading(true);
    try {
      let turnstileToken = "";
      try {
        turnstileToken = await requestTurnstileToken();
      } catch {
        toast.error("Security check failed. Refresh and try again.");
        setLoading(false);
        return;
      }
      const data = await joinWaitlist({
        handle,
        email: email.trim().toLowerCase(),
        turnstileToken,
      });
      setSubmitted(data);
      toast.success(`@${data.handle} held. Position #${data.position}.`);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Couldn't reserve right now. Try again.";
      toast.error(typeof msg === "string" ? msg : "Validation failed.");
      // Reset the widget so the user can retry with a fresh token.
      try {
        if (window.turnstile && turnstileWidgetIdRef.current !== null) {
          window.turnstile.reset(turnstileWidgetIdRef.current);
        }
      } catch {
        /* ignore */
      }
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div
        data-testid={`${id}-success`}
        className="glass-card rounded-2xl p-7 sm:p-9"
      >
        <div className="flex items-center gap-2 mb-4">
          <span className="w-7 h-7 rounded-lg bg-[#5B9EC9] text-white flex items-center justify-center">
            <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
          </span>
          <span className="label-overline">Reservation held</span>
        </div>
        <h3 className="font-serif-display text-3xl sm:text-4xl text-pearl leading-tight tracking-tight">
          <span className="italic text-bone">@{submitted.handle}</span>
          <span className="text-ash">.</span>
        </h3>
        <p className="mt-4 text-ash text-[15px] leading-relaxed">
          You&apos;re <span className="text-pearl">#{submitted.position}</span> in line.
          We&apos;ll write to <span className="text-pearl">{submitted.email}</span> the moment
          your address is ready to claim.
        </p>
        <div className="divider-hair my-6" />
        <ShareRow context={`${id}-success`} overline="Tell a friend" />
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid={id}
      className={`glass-card rounded-2xl ${compact ? "p-5 sm:p-6" : "p-6 sm:p-8"}`}
    >
      <div className="flex items-center justify-center mb-5">
        <span className="label-overline text-center leading-relaxed max-w-md" data-testid={`${id}-label`}>
          Secure your spot on W, the ultra-freemium email experience.
        </span>
      </div>

      {/* Handle */}
      <div className="flex items-baseline justify-between mb-1">
        <label className="label-overline">Desired handle</label>
        <HandleStatusBadge status={handleStatus} testid={`${id}-handle-status`} />
      </div>
      <div
        className={`flex items-stretch rounded-lg border bg-[var(--w-input-bg)] transition-colors duration-300 ${
          handleStatus === "available"
            ? "border-green-500"
            : handleStatus === "premium"
            ? "border-amber-500"
            : handleStatus === "taken" || handleStatus === "reserved" || handleStatus === "trademarked" || handleStatus === "not allowed" || handleStatus === "invalid"
            ? "border-red-500"
            : "border-hairline focus-within:border-[#5B9EC9]/50"
        }`}
      >
        <input
          data-testid={`${id}-handle-input`}
          type="text"
          value={handle}
          onChange={(e) => setHandle(sanitizeHandle(e.target.value))}
          placeholder="name"
          autoComplete="off"
          spellCheck="false"
          className="flex-1 bg-transparent outline-none text-pearl placeholder:text-ash/50 text-[15px] py-3 pl-4 font-mono-label"
        />
        <span className="px-3 flex items-center text-bone select-none text-[15px] font-mono-label border-l border-hairline">
          @w.xyz
        </span>
      </div>

      {handle && (
        <p
          data-testid={`${id}-preview`}
          className="mt-2 text-[12px] text-ash/70 font-mono-label truncate"
        >
          <span className="text-ash/50">Your address:</span>{" "}
          <span className="text-pearl">{handle}@w.xyz</span>
        </p>
      )}

      {suggestions.length > 0 && (
        <div
          data-testid={`${id}-suggestions`}
          className="mt-2.5 flex flex-wrap items-center gap-2"
        >
          <span className="label-overline text-ash/70">Try</span>
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`${id}-suggestion-${s}`}
              onClick={() => setHandle(s)}
              className="group inline-flex items-center gap-1 rounded-lg border border-hairline px-3 py-1 text-[12px] text-bone hover:text-pearl hover:border-bone/40 transition-colors font-mono-label"
            >
              @{s}
              <ArrowRight className="w-3 h-3 opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-0 transition-all duration-200" />
            </button>
          ))}
        </div>
      )}

      {/* Email */}
      <label className="block mt-5 mb-1 label-overline">Email</label>
      <div className="rounded-lg border border-hairline bg-[var(--w-input-bg)] focus-within:border-[#5B9EC9]/40 transition-colors duration-300">
        <input
          data-testid={`${id}-email-input`}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="firstmiddlelastname2004@gmail.com"
          autoComplete="email"
          className="w-full bg-transparent outline-none text-pearl placeholder:text-ash/50 text-[15px] py-3 px-4"
        />
      </div>

      <button
        type="submit"
        data-testid={`${id}-submit-btn`}
        disabled={loading || !handleOk || !emailLooksValid}
        className="group mt-6 w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#1E3A5F] text-white py-3.5 text-[14px] font-medium tracking-wide hover:bg-[#2A4A75] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> Holding…
          </>
        ) : (
          <>
            Hold this address
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </>
        )}
      </button>

      {/* Cloudflare Turnstile — invisible; renders no UI but must exist in the DOM */}
      <div
        ref={turnstileContainerRef}
        data-testid={`${id}-turnstile`}
        aria-hidden="true"
      />

      <p className="mt-4 text-[12px] text-ash/80 leading-relaxed text-center" data-testid={`${id}-handle-rules`}>
        Handles between 6 to 30 characters are complimentary. 4 to 5 character handles will be available at a premium. Handles may include letters, numbers, hyphens and periods. Lock in your handle today to guarantee availability.
      </p>
    </form>
  );
};

export default WaitlistForm;

const HandleStatusBadge = ({ status, testid }) => {
  if (status === "idle") return <span className="label-overline opacity-0">_</span>;

  const config = {
    checking: {
      icon: <Loader2 className="w-3 h-3 animate-spin" />,
      text: "checking",
      cls: "text-ash",
    },
    available: {
      icon: <Check className="w-3 h-3" strokeWidth={3} />,
      text: "available",
      cls: "text-green-600",
    },
    premium: {
      icon: <Check className="w-3 h-3" strokeWidth={3} />,
      text: "premium",
      cls: "text-amber-500",
    },
    taken: {
      icon: <X className="w-3 h-3" strokeWidth={3} />,
      text: "already held",
      cls: "text-red-500",
    },
    reserved: {
      icon: <X className="w-3 h-3" strokeWidth={3} />,
      text: "reserved",
      cls: "text-red-500",
    },
    trademarked: {
      icon: <X className="w-3 h-3" strokeWidth={3} />,
      text: "trademark protected",
      cls: "text-red-500",
    },
    invalid: {
      icon: <X className="w-3 h-3" strokeWidth={3} />,
      text: "not allowed",
      cls: "text-red-500",
    },
    "not allowed": {
      icon: <X className="w-3 h-3" strokeWidth={3} />,
      text: "not allowed",
      cls: "text-red-500",
    },
  }[status];

  if (!config) return null;
  return (
    <span
      data-testid={testid}
      data-status={status}
      className={`inline-flex items-center gap-1.5 font-mono-label text-[10px] tracking-[0.2em] uppercase ${config.cls}`}
    >
      {config.icon}
      {config.text}
    </span>
  );
};
